/**
 * Compares nested shared-model values using the same structural rules as the
 * original Creator implementation.
 *
 * @param firstValue First value.
 * @param secondValue Second value.
 * @returns Whether both values are equivalent.
 */
function compareValues(
	firstValue: unknown,
	secondValue: unknown,
): boolean {
	if (typeof firstValue !== typeof secondValue) {
		return false;
	}

	if (
		Array.isArray(firstValue)
		&& Array.isArray(secondValue)
	) {
		return firstValue.length === secondValue.length
			&& firstValue.every((value, index) => compareValues(
				value,
				secondValue[index],
			));
	}

	if (
		typeof Element !== 'undefined'
		&& firstValue instanceof Element
		&& secondValue instanceof Element
	) {
		return firstValue.isEqualNode(secondValue);
	}

	if (
		firstValue !== null
		&& secondValue !== null
		&& typeof firstValue === 'object'
		&& typeof secondValue === 'object'
		&& !Array.isArray(firstValue)
		&& !Array.isArray(secondValue)
	) {
		const firstRecord = firstValue as Record<string, unknown>;
		const secondRecord = secondValue as Record<string, unknown>;

		return Object.keys(firstRecord).every((key) => compareValues(
			firstRecord[key],
			secondRecord[key],
		));
	}

	return firstValue === secondValue;
}

/**
 * Lists properties whose values differ between two renderer models.
 *
 * @param firstObject First model.
 * @param secondObject Second model.
 * @returns Unique differing property names.
 */
export function getObjectDifferences<
	T extends object,
	K extends keyof T,
>(
	firstObject: T,
	secondObject: T,
): K[] {
	const firstKeys = Object.keys(firstObject) as K[];
	const secondKeys = Object.keys(secondObject) as K[];
	const differences = firstKeys
		.filter((key) => !compareValues(
			firstObject[key],
			secondObject[key],
		))
		.concat(secondKeys.filter((key) => !compareValues(
			secondObject[key],
			firstObject[key],
		)));

	return Array.from(new Set(differences));
}
